import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Paperclip, Clock, Star, Download, Eye, FileText, Image, File } from 'lucide-react';
import TicketHeader from './TicketHeader';
import TicketHistory from './TicketHistory';
import ReplySection from './ReplySection';
import ActionButtons from './ActionButtons';

const TicketModal = ({ ticket, onClose, onUpdate, userRole = 'customer', userId }) => {
  const [satisfactionRating, setSatisfactionRating] = useState(ticket.satisfactionRating || 0);
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const statusConfig = {
    open: { dot: 'bg-blue-500', ring: 'ring-blue-500/20', label: 'Open' },
    'in_progress': { dot: 'bg-amber-500', ring: 'ring-amber-500/20', label: 'In Progress' },
    'in progress': { dot: 'bg-amber-500', ring: 'ring-amber-500/20', label: 'In Progress' },
    resolved: { dot: 'bg-emerald-500', ring: 'ring-emerald-500/20', label: 'Resolved' },
    closed: { dot: 'bg-emerald-500', ring: 'ring-emerald-500/20', label: 'Closed' },
  };

  // Handle escape key to close modal
  React.useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Lock body scroll while modal is open
  React.useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Focus management
  React.useEffect(() => {
    const modalElement = document.getElementById('ticket-modal');
    if (modalElement) {
      modalElement.focus();
    }
  }, []);

  const handleReplySubmit = async (newReply) => {
    try {
      const updatedTicket = {
        ...ticket,
        replies: [...(ticket.replies || []), newReply],
        updatedAt: new Date()
      };

      onUpdate(updatedTicket);
    } catch (error) {
      console.error('Error submitting reply:', error);
      throw error;
    }
  };

  const handleRatingSubmit = (rating) => {
    setSatisfactionRating(rating);

    const updatedTicket = {
      ...ticket,
      satisfactionRating: rating,
      updatedAt: new Date()
    };

    onUpdate(updatedTicket);
  };

  const canRate = userRole === 'customer' &&
    (ticket.status === 'resolved' || ticket.status === 'closed');

  /* Empty until the API exposes agents. GET /support returns a ticket with
     id, date, subject, description and status — there is no assignee field
     and no endpoint that lists agents, so this list was fixtures. The
     assignment control renders its own empty state rather than offering
     people who do not exist.

     See docs/merko-api-requirement.md § Agents. */
  const availableAgents: Array<{ id: string; name: string; role: string }> = [];

  const modalContent = (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-2 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ticket-modal-title"
      aria-describedby="ticket-modal-description"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        id="ticket-modal"
        className="bg-surface dark:bg-dark-800 border border-line dark:border-white/[0.07] rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col focus:outline-none"
        tabIndex={-1}
      >
        {/* Header Component */}
        <TicketHeader
          ticket={ticket}
          onClose={onClose}
          userRole={userRole}
        />

        {/* Content */}
        <div className="flex-1 overflow-y-auto bg-surface dark:bg-dark-800">
          {/* Ticket Description */}
          <div className="p-4 sm:p-6 border-b border-line-subtle dark:border-white/[0.06]">
            <h3
              id="ticket-modal-title"
              className="text-xs font-semibold uppercase tracking-wide text-content-muted dark:text-gray-500 mb-3"
            >
              Description
            </h3>
            <div className="prose prose-sm max-w-none">
              <p
                id="ticket-modal-description"
                className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap text-sm sm:text-base leading-relaxed"
              >
                {ticket.description}
              </p>
            </div>

            {/* Tags */}
            {ticket.tags && ticket.tags.length > 0 && (
              <div className="mt-4">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-content-muted dark:text-gray-500 mb-2">Tags</h4>
                <div className="flex flex-wrap gap-2" role="list" aria-label="Ticket tags">
                  {ticket.tags.map((tag, index) => (
                    <span
                      key={index}
                      role="listitem"
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-white/[0.06] text-gray-700 dark:text-gray-300 border border-line dark:border-white/[0.07]"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Attachments */}
            {ticket.attachments && ticket.attachments.length > 0 && (
              <div className="mt-4">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-content-muted dark:text-gray-500 mb-3 flex items-center gap-2">
                  <Paperclip className="w-3.5 h-3.5" />
                  Attachments ({ticket.attachments.length})
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" role="list" aria-label="Ticket attachments">
                  {ticket.attachments.map((attachment) => {
                    const isImage = attachment.type?.startsWith('image/');
                    const isPDF = attachment.type === 'application/pdf';
                    const isDoc = attachment.type?.includes('document') || attachment.type?.includes('word');

                    const getFileIcon = () => {
                      if (isImage) return <Image className="w-5 h-5 text-blue-500 dark:text-blue-400" />;
                      if (isPDF) return <FileText className="w-5 h-5 text-red-500 dark:text-red-400" />;
                      if (isDoc) return <FileText className="w-5 h-5 text-blue-600 dark:text-blue-300" />;
                      return <File className="w-5 h-5 text-content-muted" />;
                    };

                    const formatFileSize = (bytes) => {
                      if (bytes === 0) return '0 Bytes';
                      const k = 1024;
                      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
                      const i = Math.floor(Math.log(bytes) / Math.log(k));
                      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
                    };

                    const handleDownload = () => {
                      // In a real application, this would download from a server
                      // For now, we'll simulate the download action
                      if (attachment.file) {
                        // If we have the actual file object (from recent upload)
                        const url = URL.createObjectURL(attachment.file);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = attachment.filename;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        URL.revokeObjectURL(url);
                      } else {
                        // For existing attachments, show a message (in real app, would fetch from server)
                        alert(`Downloading ${attachment.filename}...`);
                      }
                    };

                    const handlePreview = () => {
                      if (isImage && attachment.file) {
                        // For images, open in new tab
                        const url = URL.createObjectURL(attachment.file);
                        window.open(url, '_blank');
                      } else {
                        alert(`Preview for ${attachment.filename} would open here`);
                      }
                    };

                    return (
                      <div
                        key={attachment.id}
                        role="listitem"
                        className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-white/[0.03] border border-line dark:border-white/[0.07] rounded-xl hover:shadow-sm transition-all"
                      >
                        <div className="flex-shrink-0">
                          {getFileIcon()}
                        </div>

                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-content dark:text-white truncate" title={attachment.filename}>
                            {attachment.filename}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-content-muted dark:text-gray-500">
                              {formatFileSize(attachment.size)}
                            </span>
                            {attachment.uploadedAt && (
                              <span className="text-xs text-content-subtle dark:text-gray-600">
                                • {new Date(attachment.uploadedAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-1">
                          {isImage && (
                            <button
                              onClick={handlePreview}
                              className="p-2 text-content-subtle dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                              title="Preview image"
                              aria-label={`Preview ${attachment.filename}`}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          )}

                          <button
                            onClick={handleDownload}
                            className="p-2 text-content-subtle dark:text-gray-500 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
                            title="Download file"
                            aria-label={`Download ${attachment.filename}`}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Ticket History Component */}
          <TicketHistory
            ticket={ticket}
            userRole={userRole}
          />

          {/* Reply Section Component */}
          <ReplySection
            ticket={ticket}
            onReplySubmit={handleReplySubmit}
            userRole={userRole}
            userId={userId}
          />

          {/* Satisfaction Rating (for resolved/closed tickets) */}
          {canRate && (
            <div className="p-4 sm:p-6 border-b border-line-subtle dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.02]">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-content-muted dark:text-gray-500 mb-3">Rate Your Experience</h3>
              <p className="text-sm text-content-secondary dark:text-gray-400 mb-4">
                How satisfied are you with the resolution of this ticket?
              </p>
              <div
                className="flex items-center gap-1 sm:gap-2"
                role="radiogroup"
                aria-labelledby="rating-label"
                aria-describedby="rating-description"
              >
                <span id="rating-label" className="sr-only">Rate your satisfaction from 1 to 5 stars</span>
                <span id="rating-description" className="sr-only">
                  Current rating: {satisfactionRating > 0 ? `${satisfactionRating} out of 5 stars` : 'No rating selected'}
                </span>
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    onClick={() => handleRatingSubmit(rating)}
                    className={`p-1 sm:p-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 dark:focus:ring-offset-dark-800 ${satisfactionRating >= rating
                        ? 'text-yellow-400 hover:text-yellow-500'
                        : 'text-gray-300 dark:text-gray-600 hover:text-content-subtle dark:hover:text-content-muted'
                      }`}
                    role="radio"
                    aria-checked={satisfactionRating === rating}
                    aria-label={`Rate ${rating} out of 5 stars`}
                    title={`Rate ${rating} out of 5 stars`}
                  >
                    <Star className="w-5 h-5 sm:w-6 sm:h-6 fill-current" aria-hidden="true" />
                  </button>
                ))}
                {satisfactionRating > 0 && (
                  <span className="ml-2 text-sm text-content-secondary dark:text-gray-400" aria-live="polite">
                    ({satisfactionRating}/5 stars)
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons Component */}
        <ActionButtons
          ticket={ticket}
          onUpdate={onUpdate}
          userRole={userRole}
          availableAgents={availableAgents}
        />

        {/* Closed Ticket Message */}
        {(ticket.status === 'resolved' || ticket.status === 'closed') && (
          <div className="p-4 bg-emerald-50 dark:bg-emerald-900/10 border-t border-emerald-100 dark:border-emerald-900/20">
            <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
              <Clock className="w-4 h-4" />
              <span>
                This ticket has been {ticket.status}
                {ticket.resolvedAt && (
                  <span> on {formatDate(ticket.resolvedAt)}</span>
                )}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default TicketModal;